import { AccountInfo } from "@azure/msal-browser";
import { ReactNode } from "react";
import { selectUser } from "../context/slices/userProfileSlice";
import { useXNGSelector } from "../context/store";
import IfElseBox from "../design/components-dev/if_else_box";
import FullScreenLoadingMessage from "../design/high-level/fullscreen_loading_spinner";
import { signAllElectronicSignatures } from "../utils/signElectronicSignature";
import OnboardingFlowHandler from "../views/registration_flow/flow_handler";
import {
  STATEMENT_ELECTRONIC_SIGNATURE_CONSENT,
  STATEMENT_FERPA_AUTHORIZATION,
  STATEMENT_TRUE_AND_ACCURATE_DATA_AUTHORIZATION,
} from "../views/registration_flow/user_onboarding/statements";
import { FormFourValues } from "../views/registration_flow/user_onboarding/types";
import { FourthView } from "../views/registration_flow/user_onboarding/views";
import { msalInstance } from "./msalInstance";

interface OnboardingFlowProps {
  readonly children: ReactNode;
  readonly msalAccount: AccountInfo | null;
  readonly reduxPopulated: boolean;
  readonly showSignatoryView: boolean;
  readonly setShowSignatoryView: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * OnboardingFlow component manages the onboarding flow including:
 * - Electronic signature collection
 * - Loading states
 * - Conditional display of onboarding vs main application
 */
export function OnboardingFlow({
  children,
  msalAccount,
  reduxPopulated,
  showSignatoryView,
  setShowSignatoryView,
}: OnboardingFlowProps) {
  const user = useXNGSelector(selectUser);

  return (
    <IfElseBox
      if={showSignatoryView}
      then={
        <FourthView
          onValidNext={async (values: FormFourValues) => {
            const signatureData = {
              fullName: `${user?.firstName as string} ${user?.lastName as string}`,
              oid: msalAccount?.idTokenClaims?.oid as string,
              ipAddress: "",
              state: msalAccount?.idTokenClaims?.state as string,
            };
            try {
              await signAllElectronicSignatures(signatureData);
              setShowSignatoryView(false);
            } catch (err) {
              console.log("UNABLE SIGN ELECTRONIC SIGNATURE: ", err);
            }
          }}
          apiDependentValues={{
            ferpaAuthorizationStatement: STATEMENT_FERPA_AUTHORIZATION,
            electronicSignatureConsent: STATEMENT_ELECTRONIC_SIGNATURE_CONSENT,
            trueAndAccurateDataAuthorization: STATEMENT_TRUE_AND_ACCURATE_DATA_AUTHORIZATION,
          }}
        />
      }
      else={
        <IfElseBox
          if={msalAccount === null}
          then={<FullScreenLoadingMessage message="Logging you in..." />}
          else={
            <IfElseBox
              if={!reduxPopulated}
              then={<FullScreenLoadingMessage message="Gathering info..." />}
              else={
                <OnboardingFlowHandler
                  onLogout={() => msalInstance.logoutRedirect()}
                  account={msalAccount!}
                />
              }
            />
          }
        />
      }
    />
  );
}
